import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../../App';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { Zap, LogOut, Settings, Shield, Link2, Menu, X } from 'lucide-react';
import ModeToggle from '../marketing/ModeToggle';

// New nav structure per DD-07 content audit
const PUBLIC_NAV = [
  { label: 'Solutions', to: '/solutions' },
  { label: 'Examples', to: '/gallery' },
  { label: 'Playbooks', to: '/playbooks' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Enterprise', to: '/enterprise' },
  { label: 'About', to: '/about' },
];

const AUTH_NAV = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'Projects', to: '/projects' },
  { label: 'Playbooks', to: '/playbooks' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Voices', to: '/voices', feature: 'voice_library' },
  { label: 'Context', to: '/contexts' },
  { label: 'Teams', to: '/workspaces' },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [tierFeatures, setTierFeatures] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // #1137 Task 3.4: when an impersonation grant is active, the banner
  // takes the top 40px (h-10) at z-60. Push the header down so it
  // doesn't render underneath; banner sits above (z-60 vs header z-50).
  const ctxImp = useImpersonation();
  const headerTopClass = ctxImp?.impersonation ? 'top-10' : 'top-0';

  useEffect(() => {
    if (!user) return;
    API.get('/api/my-tier')
      .then(r => setTierFeatures(r.data.features || {}))
      .catch(() => {});
  }, [user?.plan, user?.role]);

  useEffect(() => {
    if (!user) return;
    const fetchPending = () => {
      API.get('/api/notifications/pending')
        .then(r => setPendingCount(r.data.total || 0))
        .catch(() => {});
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className={`fixed ${headerTopClass} left-0 right-0 z-50 glass border-b border-[rgba(245,241,232,0.08)]`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2 group shrink-0" data-testid="logo-link">
          <div className="w-7 h-7 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
            <Zap className="w-4 h-4 text-[#0A0A0A]" />
          </div>
          <span className="text-lg font-bold tracking-tight font-display uppercase text-[#F5F1E8]">
            Fray
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-0.5">
          {user ? (
            <>
              {AUTH_NAV.map(item => {
                if (item.feature && tierFeatures[item.feature] === false) return null;
                return <NavLink key={item.to} to={item.to} label={item.label} active={location.pathname === item.to} />;
              })}
              {(user.is_investor || user.role === 'fray_admin' || user.role === 'fray_super_admin') && (
                <Link
                  to="/investors"
                  className={`px-2.5 py-1.5 text-xs font-mono tracking-wider transition-colors whitespace-nowrap ${
                    location.pathname === '/investors' ? 'text-[#00D4FF]' : 'text-[#00D4FF]/60 hover:text-[#00D4FF]'
                  }`}
                  data-testid="nav-investors"
                >
                  INVESTORS
                </Link>
              )}
              {tierFeatures.cat_fight !== false && (
                <Link
                  to="/cat-fight"
                  className={`px-2.5 py-1.5 text-xs font-mono tracking-wider transition-colors whitespace-nowrap ${
                    location.pathname === '/cat-fight' ? 'text-[#EF4444]' : 'text-[#EF4444]/60 hover:text-[#EF4444]'
                  }`}
                  data-testid="nav-cat-fight"
                >
                  CAT FIGHT
                </Link>
              )}
              {tierFeatures.altered_states !== false && (
                <Link
                  to="/altered-states"
                  className={`px-2.5 py-1.5 text-xs font-mono tracking-wider transition-colors whitespace-nowrap ${
                    location.pathname === '/altered-states' ? 'text-[#7C3AED]' : 'text-[#7C3AED]/60 hover:text-[#7C3AED]'
                  }`}
                  data-testid="nav-altered-states"
                >
                  TRIP
                </Link>
              )}
              <Link
                to="/connections"
                className="relative px-2.5 py-1.5 text-xs text-[rgba(245,241,232,0.48)] hover:text-[#F5F1E8] transition-colors flex items-center gap-1 font-mono tracking-wider"
                data-testid="nav-connections"
              >
                <Link2 className="w-3.5 h-3.5" />
                {pendingCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] bg-[#FF4D00] text-[#0A0A0A] text-[9px] font-bold rounded-full flex items-center justify-center px-0.5" data-testid="pending-badge">
                    {pendingCount}
                  </span>
                )}
              </Link>
              {(user.role === 'fray_admin' || user.role === 'fray_super_admin') && (
                <Link to="/admin" className="px-2.5 py-1.5 text-xs text-[rgba(245,241,232,0.48)] hover:text-[#F5F1E8] transition-colors font-mono tracking-wider whitespace-nowrap" data-testid="nav-admin">
                  <Shield className="w-3.5 h-3.5 inline mr-0.5" />ADMIN
                </Link>
              )}
              {['super_admin', 'admin'].includes(user.role) && user.plan === 'enterprise' && (
                <Link to="/admin/enterprise" className="px-2.5 py-1.5 text-xs text-[#F2A900]/70 hover:text-[#F2A900] transition-colors font-mono tracking-wider whitespace-nowrap" data-testid="nav-workspace-admin">
                  <Shield className="w-3.5 h-3.5 inline mr-0.5" />WORKSPACE ADMIN
                </Link>
              )}

              <div className="w-px h-4 bg-[rgba(245,241,232,0.08)] mx-1.5" />
              <ModeToggle variant="nav" />
              {/* Credit balance indicator */}
              <CreditBadge tierFeatures={tierFeatures} />
              {/* Email verification chip */}
              <VerifyEmailChip />
              <div className="w-px h-4 bg-[rgba(245,241,232,0.08)] mx-1.5" />

              <Link to="/settings" className="p-1.5 text-[rgba(245,241,232,0.48)] hover:text-[#F5F1E8] transition-colors" data-testid="nav-settings">
                <Settings className="w-4 h-4" />
              </Link>
              <span className="text-[10px] text-[rgba(245,241,232,0.48)] font-mono mx-1" data-testid="user-name">{user.name}</span>
              {user.billing_mode === 'byok' && (
                <span className="text-[8px] px-1 py-0.5 bg-[#FF4D00]/10 text-[#FF4D00] border border-[#FF4D00]/20 font-mono">BYOK</span>
              )}
              <button onClick={() => { logout(); navigate('/'); }} className="p-1.5 text-[rgba(245,241,232,0.48)] hover:text-[#A61E17] transition-colors ml-1" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              {PUBLIC_NAV.map(item => (
                <NavLink key={item.to} to={item.to} label={item.label} active={location.pathname === item.to} />
              ))}
              <div className="w-px h-4 bg-[rgba(245,241,232,0.08)] mx-1.5" />
              <Link
                to="/login"
                className="px-3 py-1.5 text-sm text-[rgba(245,241,232,0.72)] hover:text-[#F5F1E8] transition-colors font-body"
                data-testid="nav-login"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className="px-5 py-1.5 text-sm bg-[#FF4D00] text-[#0A0A0A] font-display font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
                data-testid="nav-signup"
              >
                Start Debating
              </Link>
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-[rgba(245,241,232,0.72)] hover:text-[#F5F1E8] transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[rgba(245,241,232,0.08)] bg-[#0A0A0A]/95 backdrop-blur-md max-h-[calc(100vh-56px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {user ? (
              <>
                <div className="flex items-center justify-between px-3 py-2 mb-2 border-b border-[rgba(245,241,232,0.08)]">
                  <span className="text-sm text-[#F5F1E8] font-display">{user.name}</span>
                  <ModeToggle variant="nav" />
                </div>
                {AUTH_NAV.map(item => {
                  if (item.feature && tierFeatures[item.feature] === false) return null;
                  return <MobileNavLink key={item.to} label={item.label} onClick={() => navigate(item.to)} active={location.pathname === item.to} />;
                })}
                {(user.is_investor || user.role === 'fray_admin' || user.role === 'fray_super_admin') && (
                  <MobileNavLink label="Investors" onClick={() => navigate('/investors')} className="text-[#00D4FF]" />
                )}
                {tierFeatures.cat_fight !== false && (
                  <MobileNavLink label="Cat Fight" onClick={() => navigate('/cat-fight')} className="text-[#EF4444]" />
                )}
                {tierFeatures.altered_states !== false && (
                  <MobileNavLink label="Trip" onClick={() => navigate('/altered-states')} className="text-[#7C3AED]" />
                )}
                <MobileNavLink label={
                  <span className="flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> Connections
                    {pendingCount > 0 && (
                      <span className="min-w-[16px] h-[16px] bg-[#FF4D00] text-[#0A0A0A] text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                        {pendingCount}
                      </span>
                    )}
                  </span>
                } onClick={() => navigate('/connections')} />
                {(user.role === 'fray_admin' || user.role === 'fray_super_admin') && (
                  <MobileNavLink label={<span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Admin</span>} onClick={() => navigate('/admin')} />
                )}
                {['super_admin', 'admin'].includes(user.role) && user.plan === 'enterprise' && (
                  <MobileNavLink label={<span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Workspace Admin</span>} onClick={() => navigate('/admin/enterprise')} />
                )}
                <MobileNavLink label={<span className="flex items-center gap-2"><Settings className="w-4 h-4" /> Settings</span>} onClick={() => navigate('/settings')} />
                <div className="border-t border-[rgba(245,241,232,0.08)] mt-2 pt-2">
                  <button
                    onClick={() => { logout(); navigate('/'); }}
                    className="w-full flex items-center gap-2 px-3 py-3 min-h-[44px] text-sm text-[#A61E17] hover:bg-[#A61E17]/10 transition-colors font-mono"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              </>
            ) : (
              <>
                {PUBLIC_NAV.map(item => (
                  <MobileNavLink key={item.to} label={item.label} onClick={() => navigate(item.to)} active={location.pathname === item.to} />
                ))}
                <div className="border-t border-[rgba(245,241,232,0.08)] mt-2 pt-2 space-y-2">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full px-3 py-3 min-h-[44px] text-sm text-[rgba(245,241,232,0.72)] hover:text-[#F5F1E8] transition-colors text-left font-body"
                    data-testid="mobile-login-btn"
                  >
                    Log in
                  </button>
                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full px-3 py-3 min-h-[44px] text-sm bg-[#FF4D00] text-[#0A0A0A] font-display font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors text-center"
                    data-testid="mobile-signup-btn"
                  >
                    Start Debating
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function NavLink({ to, label, active }) {
  return (
    <Link
      to={to}
      className={`px-2.5 py-1.5 text-xs font-mono tracking-wider uppercase transition-colors whitespace-nowrap ${
        active
          ? 'text-[#FF4D00]'
          : 'text-[rgba(245,241,232,0.48)] hover:text-[#F5F1E8]'
      }`}
      data-testid={`nav-${label.toLowerCase()}`}
    >
      {label}
    </Link>
  );
}

function VerifyEmailChip() {
  const [show, setShow] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    API.get('/api/verification/status')
      .then(r => { if (r.data.needs_verification) setShow(true); })
      .catch(() => {});
  }, []);

  const sendVerification = async () => {
    setSending(true);
    try {
      await API.post('/api/verification/send');
      setShow(false);
    } catch { /* ignore */ }
    setSending(false);
  };

  if (!show) return null;

  return (
    <button onClick={sendVerification} disabled={sending}
      className="px-2 py-0.5 text-[9px] font-mono bg-[#F2A900]/10 text-[#F2A900] border border-[#F2A900]/30 hover:bg-[#F2A900]/20 transition-colors"
      data-testid="verify-email-chip">
      {sending ? '...' : 'Verify email'}
    </button>
  );
}

function CreditBadge({ tierFeatures }) {
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    API.get('/api/credits/balance')
      .then(r => {
        if (!r.data.enterprise_exempt) setCredits(r.data);
      })
      .catch(() => {});
  }, []);

  if (!credits) return null;

  const color = credits.warning_tier === 'exhausted' ? '#A61E17'
    : credits.warning_tier === 'critical' ? '#A61E17'
    : credits.warning_tier === 'low' ? '#F2A900'
    : 'rgba(245,241,232,0.48)';

  return (
    <Link to="/settings" className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono" style={{ color }}
      data-testid="credit-badge" title={`${credits.balance} credits remaining`}>
      <span>{credits.balance}</span>
      <span className="opacity-50">cr</span>
    </Link>
  );
}

function MobileNavLink({ label, onClick, className = '', active }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 min-h-[44px] text-sm transition-colors font-mono tracking-wider ${
        active
          ? 'text-[#FF4D00] bg-[#FF4D00]/5'
          : 'text-[rgba(245,241,232,0.72)] hover:text-[#F5F1E8] hover:bg-[#1E1E1E]'
      } ${className}`}
      data-testid={`mobile-nav-${typeof label === 'string' ? label.toLowerCase() : 'link'}`}
    >
      {label}
    </button>
  );
}
